import React from 'react';

import { CourseImageConstants } from '../../constants/CourseImageConstants';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';

// Courses without their own photo share one default header, so a screenful of them at once reads
// as tiled wallpaper rather than as photography. Shifting the crop by a hash of the course id
// breaks that up: same image, different band of it, and stable for a given course across renders
// and re-sorts.
//
// Only ever applied to the default. A club's own header is framed by whoever uploaded it, so
// sliding the crop off centre would cut the subject out of a photo chosen deliberately.
const heroObjectPosition = (id: string | undefined): string => {
  let hash = 0;
  for (let i = 0; i < (id?.length ?? 0); i += 1) {
    hash = (hash * 31 + (id as string).charCodeAt(i)) % 100000;
  }
  // Kept inside 20–80% so the crop never slides to a bare edge of the photo.
  return `50% ${20 + (hash % 61)}%`;
};

interface CourseHeroProps {
  course: CourseMinifiedDTO;
  // Already formatted, because the two surfaces label it differently: the list card says
  // "This course" for the club the list is built around, the drawer always says a distance. Null
  // renders no pill at all.
  distanceLabel: string | null;
  // The list card scrolls into view normally, so its hero can load lazily. The drawer's cannot:
  // it is parked off-viewport with translate-x-full, and a lazy image there is not fetched until
  // the drawer opens — so the first open slides in with an empty hero.
  eager?: boolean;
}

// The photo block at the top of a course surface: header image, scrim, distance pill and the club
// logo on a white plate overlapping the bottom edge.
//
// Shared by CourseCard and CourseDetailDrawer, which previously kept two copies of this markup and
// shared only the crop function. Nothing in here is interactive, which is what makes it safe to
// share — the card wraps its own copy in a "select this course" <button>, and a button may not
// contain one.
//
// Whatever renders this owes the content below it enough top padding to clear the plate, which
// hangs 1.25rem past the bottom edge. Both callers use pt-8.
const CourseHero: React.FC<CourseHeroProps> = ({
  course,
  distanceLabel,
  eager = false,
}) => (
  <div className="relative h-40 w-full shrink-0 sm:h-44">
    {/* headerImageUrl arrives with the course data, resolved once server-side when the Explore
        snapshot is built — same path as logoUrl, and for the same reason: the public map cannot
        call the authenticated GET /api/file/{id}. Most courses still have none and fall back to
        the shared default header.
        The scrim is not decoration: it keeps the white logo plate and the distance pill legible
        over a bright photo, and ties the hero into the dark body below it. */}
    <img
      src={course.headerImageUrl ?? CourseImageConstants.DEFAULT_HEADER}
      alt=""
      className="h-full w-full object-cover"
      style={
        course.headerImageUrl
          ? undefined
          : { objectPosition: heroObjectPosition(course.id) }
      }
      draggable={false}
      loading={eager ? 'eager' : 'lazy'}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/45" />

    {/* Distance rides on the hero instead of the text block, so the name and location sit on the
        same lines on every card in a row. */}
    {distanceLabel != null && (
      <span className="absolute right-3 top-3 rounded-full border border-primary/30 bg-charcoal/80 px-2.5 py-1 text-[11px] font-semibold text-primary backdrop-blur-sm">
        {distanceLabel}
      </span>
    )}

    {/* object-contain with padding: these are wordmarks on their own backgrounds, so cropping or
        stretching them mangles the brand. The plate is always white, and it is never empty — a
        course with no logo of its own gets the GolfPlayed mark. Both that mark and real club logos
        are dark ink on white, so one white plate suits every case and a grid of them stays even. */}
    <div className="absolute -bottom-5 left-4 flex h-[5.5rem] w-[5.5rem] items-center justify-center overflow-hidden rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/20 sm:h-[6.6rem] sm:w-[6.6rem]">
      {/* logoUrl arrives with the course data, resolved once server-side when the Explore
          snapshot is built. It used to be fetched per surface via FileClient.getFileUrl, which
          requires auth — on the public map that returned 401 and the axios interceptor bounced
          anonymous visitors to /login on page load. */}
      <img
        // A closed course shows the shared Closed artwork instead (utils/courseClosure.ts).
        src={courseLogo(course).src}
        // Empty alt when the plate is showing a stand-in (the GolfPlayed mark, or Closed) rather
        // than this club's own logo: naming the club would describe an image that is not it.
        alt={courseLogo(course).hasOwnLogo ? course.name : ''}
        className="h-full w-full object-contain"
        draggable={false}
        loading={eager ? 'eager' : 'lazy'}
      />
    </div>
  </div>
);

export default CourseHero;
